import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Bot,
  ChevronsDown,
  Crown,
  Info,
  UserCheck,
  UserMinus,
} from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Checkbox } from "../../components/Checkbox/Checkbox";
import { Menu, type MenuItem, type MenuSeparator } from "../../components/Menu/Menu";
import { Modal } from "../../components/Modal/Modal";
import { Pill } from "../../components/Pill/Pill";
import { useOptionalToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { kickMember, updateMemberRole } from "./api";
import { formatRelativeLong } from "./relativeTime";
import type { GuildDetail, GuildMember } from "./types";
import styles from "./GuildMembersTable.module.css";

interface GuildMembersTableProps {
  projectId: string;
  guildId: string;
  /** The loaded guild — supplies `members` and `name` (used in modal copy). */
  guild: GuildDetail;
  /** Called after any successful member mutation so the parent re-fetches. */
  onChanged: () => void;
}

/** A pending confirm-modal action — kick a member or transfer leadership. */
type PendingAction = { member: GuildMember; action: "kick" | "leader" };

const ROLE_LABEL: Record<GuildMember["role"], string> = {
  leader: "Leader",
  officer: "Officer",
  member: "Member",
};

/**
 * The Members section of `GuildDetailPage`. Renders the guild roster with a
 * role-contextual per-row action menu (mockup screens 1-2). Promote / Demote
 * apply directly; Kick and Make-leader route through a confirm modal (mockup
 * screens 3-4). After any successful mutation the parent re-fetches via
 * `onChanged`.
 *
 * The roster arrives whole in one response, so `Show bots` filters it here
 * rather than asking the server again. Off by default, for the reason the
 * Players page is: «who is real in here» is the question that gets asked, and
 * the simulation is scenery the rest of the time. While they are hidden the
 * bots have no rows — and therefore no action menus: kicking one means showing
 * it first.
 */
export function GuildMembersTable({
  projectId,
  guildId,
  guild,
  onChanged,
}: GuildMembersTableProps) {
  const toast = useOptionalToast();
  const [pending, setPending] = useState<PendingAction | null>(null);
  // Error surfaced inside the open confirm modal.
  const [modalError, setModalError] = useState<string | null>(null);

  // The URL owns the toggle, as it does on Players — a roster somebody is
  // looking at survives a reload and travels in a pasted link. Written the same
  // way too: on is `bots=1`, off is the parameter being absent.
  const [searchParams, setSearchParams] = useSearchParams();
  const showBots = searchParams.get("bots") === "1";

  function setShowBots(next: boolean) {
    setSearchParams((prev) => {
      const q = new URLSearchParams(prev);
      if (next) q.set("bots", "1");
      else q.delete("bots");
      return q;
    });
  }

  const currentLeader = guild.members.find((m) => m.role === "leader");

  const realMembers = guild.members.filter((m) => !m.is_bot);
  const hiddenBots = guild.members.length - realMembers.length;
  const visibleMembers = showBots ? guild.members : realMembers;

  /** Translate any thrown error into a human message. */
  function messageFor(err: unknown): string {
    return err instanceof ApiError
      ? describeError(err.code)
      : describeError("internal");
  }

  // Direct promote / demote — applies immediately, no modal.
  const roleMutation = useMutation({
    mutationFn: ({
      playerId,
      role,
    }: {
      playerId: string;
      role: "leader" | "officer" | "member";
    }) => updateMemberRole(projectId, guildId, playerId, role),
    onSuccess: () => onChanged(),
    onError: (err) => {
      toast?.push({ variant: "danger", message: messageFor(err) });
    },
  });

  // Confirm-modal mutation — kick OR transfer leadership.
  const confirmMutation = useMutation({
    mutationFn: (p: PendingAction) =>
      p.action === "kick"
        ? kickMember(projectId, guildId, p.member.player_id)
        : updateMemberRole(
            projectId,
            guildId,
            p.member.player_id,
            "leader",
          ).then(() => undefined),
    onSuccess: () => {
      setPending(null);
      setModalError(null);
      onChanged();
    },
    onError: (err) => {
      setModalError(messageFor(err));
    },
  });

  function closeModal() {
    if (confirmMutation.isPending) return;
    setPending(null);
    setModalError(null);
  }

  function menuItems(m: GuildMember): (MenuItem | MenuSeparator)[] {
    const items: (MenuItem | MenuSeparator)[] = [];
    if (m.role === "member") {
      items.push({
        label: "Promote to officer",
        icon: <UserCheck size={14} aria-hidden="true" />,
        onSelect: () =>
          roleMutation.mutate({ playerId: m.player_id, role: "officer" }),
      });
    } else {
      items.push({
        label: "Demote to member",
        icon: <ChevronsDown size={14} aria-hidden="true" />,
        onSelect: () =>
          roleMutation.mutate({ playerId: m.player_id, role: "member" }),
      });
    }
    items.push({
      label: "Make leader",
      icon: <Crown size={14} aria-hidden="true" />,
      onSelect: () => {
        setModalError(null);
        setPending({ member: m, action: "leader" });
      },
    });
    items.push({ separator: true });
    items.push({
      label: "Kick member",
      icon: <UserMinus size={14} aria-hidden="true" />,
      variant: "danger",
      onSelect: () => {
        setModalError(null);
        setPending({ member: m, action: "kick" });
      },
    });
    return items;
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Members</h3>
        <div className={styles.headerRight}>
          {/* Both numbers in every state of the checkbox. The living count is
              what this section was rebuilt for, so it leads and it is the bold
              one; the total stands beside it so that a roster of three rows out
              of forty-eight can never be misread as a guild of three. */}
          <span className={styles.cardSub}>
            <b>{realMembers.length.toLocaleString()} real</b> ·{" "}
            {guild.member_count.toLocaleString()} total
          </span>
          {/* Always drawn, bots or none: a control that came and went between
              two guilds of the same project would be a worse surprise than a
              checkbox that does nothing on this one. */}
          <Checkbox
            checked={showBots}
            onChange={setShowBots}
            label="Show bots"
          />
        </div>
      </div>

      {/* Only when something is actually out of sight, and only above a table:
          a strip saying «0 hidden» would be noise, and one stacked on the empty
          state below would say twice what that state says better. */}
      {!showBots && hiddenBots > 0 && visibleMembers.length > 0 && (
        <div className={styles.rosterNote} role="status">
          <Info
            size={13}
            aria-hidden="true"
            className={styles.rosterNoteIcon}
          />
          <span>
            <b>
              {hiddenBots.toLocaleString()} simulated{" "}
              {hiddenBots === 1 ? "member is" : "members are"} hidden.
            </b>{" "}
            The guild is not empty — tick{" "}
            <button
              type="button"
              className={styles.noteLink}
              onClick={() => setShowBots(true)}
            >
              Show bots
            </button>{" "}
            to see the whole roster.
          </span>
        </div>
      )}

      {/* «Nobody real in here» is not «nobody in here», and the difference is
          the whole point of the section: a guild always has a leader, so an
          empty table can only ever mean the simulation is hidden. There is no
          second empty state for the table to fall into. */}
      {visibleMembers.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.ei}>
            <Bot size={26} aria-hidden="true" />
          </div>
          <h3>No real players in this guild</h3>
          <p>
            All <b>{guild.member_count.toLocaleString()}</b> members are
            simulated. The roster is not empty — the simulation is just hidden.
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowBots(true)}
          >
            Show bots
          </Button>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: "50%" }}>Member</th>
              <th>Role</th>
              <th>Joined</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {visibleMembers.map((m) => (
              <tr key={m.player_id}>
                <td>
                  <div className={styles.memberCell}>
                    <Avatar name={m.display_name} size={30} />
                    <Link
                      to={`/projects/${projectId}/players/${m.player_id}`}
                      className={styles.link}
                    >
                      {m.display_name}
                    </Link>
                    {/* Only worth marking a row once the unmarked rows are not
                        all the same thing. The same Pill the Players row and the
                        chat feed draw. */}
                    {showBots && m.is_bot && (
                      <Pill size="sm">
                        <span className={styles.botMark}>
                          <Bot size={10} aria-hidden="true" />
                          Bot
                        </span>
                      </Pill>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`${styles.roleBadge} ${styles[m.role]}`}>
                    {ROLE_LABEL[m.role]}
                  </span>
                </td>
                <td className={styles.muted}>
                  {formatRelativeLong(m.joined_at)}
                </td>
                <td className={styles.act}>
                  {m.role !== "leader" && (
                    <Menu
                      aria-label="Member actions"
                      items={menuItems(m)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pending && pending.action === "kick" && (
        <KickModal
          member={pending.member}
          guild={guild}
          error={modalError}
          busy={confirmMutation.isPending}
          onClose={closeModal}
          onConfirm={() => confirmMutation.mutate(pending)}
        />
      )}
      {pending && pending.action === "leader" && (
        <MakeLeaderModal
          member={pending.member}
          currentLeaderName={currentLeader?.display_name ?? "the current leader"}
          error={modalError}
          busy={confirmMutation.isPending}
          onClose={closeModal}
          onConfirm={() => confirmMutation.mutate(pending)}
        />
      )}
    </div>
  );
}

/**
 * Kick-member confirm modal — mockup screen 2 of
 * docs/mockups/guild-blocked-players.html.
 *
 * The copy no longer branches on visibility. It used to promise that an open
 * guild's kick could be walked straight back through, and that setting the
 * guild to Invite only first was how you made a removal stick; a kick now
 * blocks the player at every door the guild has, whichever visibility it is
 * wearing, so there is one sentence for all three.
 */
function KickModal({
  member,
  guild,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  member: GuildMember;
  guild: GuildDetail;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Kick ${member.display_name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<UserMinus size={14} aria-hidden="true" />}
          >
            Kick member
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        <b>{member.display_name}</b> will be removed from <b>{guild.name}</b>{" "}
        and blocked from returning — they can&apos;t join or apply again,
        whatever the guild&apos;s visibility. The only way back is a fresh
        invite, or an unblock from the Blocked list below.
      </p>
      {error && (
        <p className={styles.modalBody} role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </Modal>
  );
}

/** Make-leader confirm modal — mockup screen 4. */
function MakeLeaderModal({
  member,
  currentLeaderName,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  member: GuildMember;
  currentLeaderName: string;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Make ${member.display_name} the leader?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<Crown size={14} aria-hidden="true" />}
          >
            Make leader
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        <b>{member.display_name}</b> becomes the guild leader.{" "}
        <b>{currentLeaderName}</b> — the current leader — is demoted to officer.
        Takes effect immediately.
      </p>
      {error && (
        <p className={styles.modalBody} role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
